import { Component, effect, inject, Injector, signal, OnInit, computed, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Router, NavigationEnd, NavigationCancel, NavigationError, ActivatedRoute, ActivatedRouteSnapshot, Route } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, NgIf } from '@angular/common';
import { RouterModule } from '@angular/router';
import { I18nService } from '../../core/services/i18n.service';
import { MatButtonModule } from '@angular/material/button';
import { I18nTranslatePipe } from '../../core/pipes/i18n-translate.pipe';
import { Images } from '../../core/constants/image.constants';
import { GlobalHeader } from '../../shared/global-header/global-header';
import { Shared } from '../../core/services/shared';
import { AuthService } from '../../core/services/auth.service';
import { QuoteDrawerComponent } from '../../features/quote-builder/quote-drawer/quote-drawer.component';
import { QuoteDrawerService } from '../../features/quote-builder/services/quote-drawer.service';
import { FeatureFlagService } from '../../core/services/feature-flag.service';
import { FEATURE_FLAGS } from '../../core/constants/feature-flags.constants';
import { IS_HEADER_PRODUCT_SWITCH_ENABLED, PRODUCT_CATALOGUE } from '../../core/constants/product.constants';
import { clientToken, logoutRedirect, saveToStorage, selectedProductCode } from '../../core/constants/auth-state';
import type { ProductDefinition } from '../../core/models/product.model';
import { UserPermissionService } from '../../core/services/user-permission.service';
import { agentObjectSignal, currentUserProfileSignal } from '../../core/constants/auth-state';
import { ApiService } from '../../core/services/api.service';
import { ViewUserDetails } from '../../features/user-management/view-user-details/view-user-details';
import { UserManagementRow } from '../../features/user-management/user-management.constant';
import { DateTimeConversionHelper } from '../../shared/helper/date-time-conversion.helper';
import { AuditDetailsPanel } from "../../features/audit-trail/audit-details-panel/audit-details-panel";
import { ReviewQueueSharedService } from '../../features/services/review-queue-shared.service';
import { MatDialog } from '@angular/material/dialog';

interface NavItem {
  icon: string;
  title: string;
  routerLink: string;
  featureFlag?: string | string[]; // Optional feature flag(s) to control visibility
  requiredPermissions?: string[]; // Show only when user has at least one of these permissions
  allowedRoles?: string[];
  notAllowedRoles?: string[];
}

interface BreadcrumbNode {
  label: string;
  route: string;
}

@Component({
  selector: 'app-main-navbar',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    I18nTranslatePipe,
    GlobalHeader,
    QuoteDrawerComponent,
    ViewUserDetails,
    NgIf,
    AuditDetailsPanel
  ],
  templateUrl: './main-navbar.component.html',
  styleUrl: './main-navbar.component.scss',
})
export class MainNavbarComponent implements OnInit {
  protected selectedTopNavIndex = signal<number>(0);
  protected isQuoteBuilderRoute = signal<boolean>(false);
  protected isMafRoute = signal<boolean>(false);
  protected isComparisonLink = signal<boolean>(false);
  protected isMotorRoute = signal<boolean>(false);
  protected isDrawerOpen = signal<boolean>(window.innerWidth > 767);
  private currentUrl = '';
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(Injector);
  private readonly reviewQueueSharedService = inject(ReviewQueueSharedService);
  private readonly dialog = inject(MatDialog);
  @ViewChild('auditDrawer', { read: ElementRef }) private auditDrawerRef?: ElementRef<HTMLElement>;

  ngOnInit(): void {
    // Initialize user permissions so isAgent computed signal is populated
    void this.userPermissionService.initializeUserPermissions();

    this.currentUrl = this.router.url;
    this.syncNavWithRoute(this.router.url);
    this.checkRoute(this.router.url);
    this.updateHeaderBreadcrumbFromRoute();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const previousUrl = this.currentUrl;
        this.currentUrl = event.urlAfterRedirects;
        this.syncNavWithRoute(event.urlAfterRedirects);
        this.checkRoute(event.urlAfterRedirects);
        this.updateHeaderBreadcrumbFromRoute();
        this.handleProfileDrawerRouteChange(previousUrl, event.urlAfterRedirects);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.syncNavWithRoute(this.router.url);
      }
    });

    // On mobile, close drawer when compare table opens
    effect(() => {
      if (this.quoteDrawerService.mobileCompareActive() && window.innerWidth <= 767) {
        this.isDrawerOpen.set(false);
      }
    }, { injector: this.injector });

    // Sync navigation selected index dynamically when topNavItems update (e.g. after permissions load)
    effect(() => {
      this.syncNavWithRoute(this.router.url);
    }, { injector: this.injector, allowSignalWrites: true });
  }

  private checkRoute(url: string): void {
    const isQuoteBuilderRoute = url.includes('/quote-builder');
    const isComparisonLink = url.includes('/comparison');
    const isAuditRoute = url.includes('/audit-trail');
    const isReviewMafOnlineRoute = this.isReviewMafOnlineRoute(url);
    const isMotorRoute = url.startsWith('/motor');

    this.isQuoteBuilderRoute.set(isQuoteBuilderRoute);
    this.isMafRoute.set(url.includes('/maf') && !url.includes('/maf-template-designer') && !isReviewMafOnlineRoute);
    this.isComparisonLink.set(isComparisonLink);
    this.isMotorRoute.set(isMotorRoute);
    if (isAuditRoute && window.innerWidth > 767) {
      if (this.quoteDrawerService.rightDrawerMode() === 'audit') {
        this.isDrawerOpen.set(true);
        this.quoteDrawerService.openAuditDrawer();
      }
    }
    if ((isQuoteBuilderRoute || isComparisonLink) && window.innerWidth > 767) {
      this.isDrawerOpen.set(true);
      if (this.quoteDrawerService.rightDrawerMode() !== 'profile' && this.quoteDrawerService.rightDrawerMode() !== 'audit') {
        this.quoteDrawerService.openQuoteDrawer();
      }
    } else if (this.quoteDrawerService.rightDrawerMode() !== 'profile' && this.quoteDrawerService.rightDrawerMode() !== 'audit') {
      this.quoteDrawerService.clearRightDrawer();
    }
  }

  private syncNavWithRoute(url: string): void {
    const items = this.topNavItems();
    const idx = items.findIndex(item => url.startsWith(item.routerLink) && item.routerLink !== '/');
    if (idx !== -1) {
      this.selectedTopNavIndex.set(idx);
    } else if (url === '/' || url === '') {
      this.selectedTopNavIndex.set(0);
    }
  }

  private updateHeaderBreadcrumbFromRoute(): void {
    const routePath = this.router.url.split(/[?#]/, 1)[0];
    // These Motor screens have no breadcrumb in Figma: the lists, and Upload Documents, whose own header shows
    // "Quote Selection & Application > Upload Documents".
    if (/^\/motor\/((pipeline|lead-portfolio|user-management)\/?|upload-documents\/[^/]+\/?)$/.test(routePath)) {
      this.shared.clearHeaderBreadcrumb();
      return;
    }

    const breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.snapshot);
    if (breadcrumbs.length === 0) {
      this.shared.clearHeaderBreadcrumb();
      return;
    }

    const leadId =
      this.reviewQueueSharedService.selectedLeadData()?.lead_Id ??
      this.getRouteParam(this.activatedRoute.snapshot, 'leadId');
    const contactName = this.getRouteQueryParam(this.activatedRoute.snapshot, 'contactName');

    const isApplicationReview = breadcrumbs[breadcrumbs.length - 1]?.label === 'Application Review';
    const resolvedBreadcrumbs = isApplicationReview
      ? this.applyApplicationReviewEntrySource(breadcrumbs)
      : breadcrumbs;

    const items = resolvedBreadcrumbs.map((breadcrumb, index) => {
      const isCurrent = index === resolvedBreadcrumbs.length - 1;
      const label = this.resolveBreadcrumbLabel(breadcrumb.label, leadId, contactName);

      return {
        label,
        route: isCurrent ? undefined : breadcrumb.route,
      };
    });
    const current = items[items.length - 1];
    const previous = items.length > 1 ? items[items.length - 2] : undefined;

    this.shared.setHeaderBreadcrumb({
      previousLabel: previous?.label ?? '',
      previousRoute: previous?.route,
      currentLabel: current.label,
      items,
    });
  }

  private applyApplicationReviewEntrySource(breadcrumbs: BreadcrumbNode[]): BreadcrumbNode[] {
    const sourceMap: Record<string, BreadcrumbNode> = {
      pipeline: { label: 'Lead Pipeline', route: '/pipeline' },
      leadManagement: { label: 'Lead Portfolio', route: '/lead-management' },
      reviewQueue: { label: 'Review Queue', route: '/review-queue' },
    };

    const previous = sourceMap[this.reviewQueueSharedService.entrySource()] ?? sourceMap['reviewQueue'];
    return [previous, breadcrumbs[breadcrumbs.length - 1]];
  }

  private getRouteParam(snapshot: ActivatedRouteSnapshot, paramName: string): string | null {
    let currentSnapshot: ActivatedRouteSnapshot | null = snapshot;

    while (currentSnapshot) {
      const value = currentSnapshot.paramMap.get(paramName);
      if (value) {
        return value;
      }

      currentSnapshot = currentSnapshot.firstChild;
    }

    return null;
  }

  private getRouteQueryParam(snapshot: ActivatedRouteSnapshot, paramName: string): string | null {
    let currentSnapshot: ActivatedRouteSnapshot | null = snapshot;

    while (currentSnapshot) {
      const value = currentSnapshot.queryParamMap.get(paramName);
      if (value) {
        return value;
      }

      currentSnapshot = currentSnapshot.firstChild;
    }

    return null;
  }

  private resolveBreadcrumbLabel(label: string, leadId: string | null, contactName: string | null): string {
    if (label === 'Application Review' && leadId) {
      return `${label} (${leadId})`;
    }

    if (label === 'Contact Details' && contactName) {
      return contactName;
    }

    return label;
  }

  private buildBreadcrumbs(snapshot: ActivatedRouteSnapshot): BreadcrumbNode[] {
    const breadcrumbs: BreadcrumbNode[] = [];
    let currentSnapshot: ActivatedRouteSnapshot | null = snapshot;
    const routeParts: string[] = [];

    while (currentSnapshot) {
      const urlParts = currentSnapshot.url.map(segment => segment.path).filter(Boolean);
      if (urlParts.length > 0) {
        routeParts.push(...urlParts);
      }

      const label = currentSnapshot.data['breadcrumb'] as string | undefined;
      if (label) {
        // A route may name its own parent crumb (data.breadcrumbParent), replacing the ancestors' crumbs.
        const parent = currentSnapshot.data['breadcrumbParent'] as BreadcrumbNode | undefined;
        if (parent) {
          breadcrumbs.splice(0, breadcrumbs.length, parent);
        }

        const route = label === 'Motor Insurance'
          ? '/motor/lead-portfolio'
          : `/${routeParts.join('/')}`;
        const previous = breadcrumbs[breadcrumbs.length - 1];

        if (previous?.label !== label || previous.route !== route) {
          breadcrumbs.push({
            label,
            route,
          });
        }
      }

      currentSnapshot = currentSnapshot.firstChild;
    }

    return breadcrumbs;
  }

  get headerTitle(): string {
    const items = this.topNavItems();
    return items[this.selectedTopNavIndex()]?.title || '';
  }

  get headerUnsavedChangesDialogConfig(): {
    title?: string;
    message?: string;
    stayLabel?: string;
    leaveLabel?: string;
    stayFirst?: boolean;
  } | null {
    if (
      this.router.url.includes('/maf-offline-upload') ||
      this.router.url.includes('/maf-online-upload')
    ) {
      return {
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Please save before proceeding to avoid losing your data.',
        stayLabel: 'Stay on Page',
        leaveLabel: 'Leave Without Saving',
        stayFirst: true,
      };
    }

    return null;
  }

  protected readonly i18n = inject(I18nService);
  protected readonly shared = inject(Shared);
  protected readonly authService = inject(AuthService);
  protected readonly quoteDrawerService = inject(QuoteDrawerService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly userPermissionService = inject(UserPermissionService);

  // True when user has LeadInfo.View permission
  protected readonly isAgent = computed(() => this.userPermissionService.hasPermission('LeadInfo.View'));

  images = Images;

  // All navigation items with optional feature flags
  private allTopNavItems = signal<NavItem[]>([
    {
      icon: 'home_icon',
      title: 'NAV.HOME',
      routerLink: '/home',
      featureFlag: [
        FEATURE_FLAGS.AGENT_DASHBOARD,
        FEATURE_FLAGS.HOME_DASHBOARD,
      ],
      notAllowedRoles: ['SystemAdmin']
    },
    {
      icon: 'pipeline_icon',
      title: 'NAV.SALES_PIPELINE',
      routerLink: '/pipeline',
      featureFlag: FEATURE_FLAGS.LEAD_PIPELINE,
      requiredPermissions: ['LeadInfo.Manage'],
    },
    {
      icon: 'icon-lead-management',
      title: 'NAV.LEAD_MANAGEMENT',
      routerLink: '/lead-management',
      featureFlag: FEATURE_FLAGS.LEAD_DETAILS,
      requiredPermissions: ['LeadInfo.Review'],
    },
    {
      icon: 'quote-product-library',
      title: 'NAV.QUOTE_MANAGEMENT',
      routerLink: '/quote-management',
      featureFlag: FEATURE_FLAGS.QUOTE_MANAGEMENT,
      requiredPermissions: ['QuoteManagement.View'],
    },
    {
      icon: 'review_queue_icon',
      title: 'NAV.REVIEW_QUEUE',
      routerLink: '/review-queue',
      requiredPermissions: ['ReviewQueue.View'],
      featureFlag: FEATURE_FLAGS.REVIEW_QUEUE,
    },
    {
      icon: 'knowledge_hub_icon',
      title: 'NAV.POLICY_MANAGEMENT',
      routerLink: '/policy-management',
      featureFlag: FEATURE_FLAGS.POLICY_MANAGEMENT,
      requiredPermissions: ['Policy.View'],
    },
    {
      icon: 'performance_icon',
      title: 'NAV.USER_MANAGEMENT',
      routerLink: '/user-management',
      requiredPermissions: ['User.ViewAll'],
      featureFlag: FEATURE_FLAGS.USER_MANAGEMENT,
    },
    {
      icon: 'contact_management_icon',
      title: 'NAV.CONTACT_MANAGEMENT',
      routerLink: '/contact-management',
      requiredPermissions: ['ContactManagement.View'],
      featureFlag: FEATURE_FLAGS.CONTACT_MANAGEMENT,
    },
    {
      icon: 'knowledge_hub_icon',
      title: 'NAV.RENEWALS_TRACKER',
      routerLink: '/renewals-tracker',
      // featureFlag: FEATURE_FLAGS.RENEWALS_TRACKER,
    },
    {
      icon: 'knowledge_hub_icon',
      title: 'NAV.KNOWLEDGE_HUB',
      routerLink: '/',
      featureFlag: FEATURE_FLAGS.KNOWLEDGE_HUB,
    },
    {
      icon: 'audit_icon',
      title: 'NAV.AUDIT_LOG',
      routerLink: '/audit-trail',
      requiredPermissions: ['AuditTrail.View'],
      featureFlag: FEATURE_FLAGS.AUDIT_TRAIL,
    },
    {
      icon: 'knowledge_hub_icon',
      title: 'NAV.MAF_TEMPLATE_DESIGNER',
      routerLink: '/maf-template-designer',
      featureFlag: FEATURE_FLAGS.MAF_TEMPLATE_DESIGNER,
    },
  ]);

  private motorTopNavItems = signal<NavItem[]>([
    {
      icon: 'pipeline_icon',
      title: 'NAV.SALES_PIPELINE',
      routerLink: '/motor/pipeline',
      requiredPermissions: ['LeadInfo.Manage'],
    },
    {
      icon: 'icon-lead-management',
      title: 'NAV.LEAD_MANAGEMENT',
      routerLink: '/motor/lead-portfolio',

    },
    {
      icon: 'performance_icon',
      title: 'NAV.USER_MANAGEMENT',
      routerLink: '/motor/user-management',
      requiredPermissions: ['User.ViewAll'],
      featureFlag: FEATURE_FLAGS.USER_MANAGEMENT,
    },
  ]);

  topNavItems = computed(() => {
    const navItems = this.isMotorRoute()
      ? this.motorTopNavItems()
      : this.allTopNavItems();

    return navItems.filter(item => {
      const featureFlag = item.featureFlag;
      const requiredPermissions = item.requiredPermissions;

      if (
        item.routerLink === '/home' &&
        this.userPermissionService.hasPermission('ReviewQueue.Manage')
      ) {
        return false;
      }

      // DEFAULT: visible
      let isVisible = true;

      // Permission check — user must have at least one of the required permissions
      if (requiredPermissions && requiredPermissions.length > 0) {
        isVisible = requiredPermissions.some(perm => this.userPermissionService.hasPermission(perm));
      }

      // FEATURE FLAG CHECK — only evaluated when still visible
      if (isVisible && featureFlag) {
        isVisible = this.isAnyFeatureEnabled(featureFlag);
      }

      if (isVisible && item.routerLink.includes('user-management')) {
        isVisible = this.hasActiveProductAccess();
      }

      return isVisible;
    });
  });

  private hasActiveProductAccess(): boolean {
    const products = currentUserProfileSignal()?.insuranceProducts ?? [];
    if (!products.length) {
      return true;
    }

    const requiredProduct = this.isMotorRoute() ? 'Motor' : 'RetailHealth';
    return products.some(product => String(product).toLowerCase() === requiredProduct.toLowerCase());
  }

  private isAnyFeatureEnabled(featureFlag: string | string[]): boolean {
    const featureFlags = Array.isArray(featureFlag) ? featureFlag : [featureFlag];

    return featureFlags.some(flag =>
      this.featureFlagService.isFeatureEnabled(flag)
    );
  }

  bottomNavItems = signal<NavItem[]>([
    {
      icon: 'settings',
      title: 'NAV.SETTINGS',
      routerLink: '/',
    },
    {
      icon: 'help_outline',
      title: 'NAV.HELP_SUPPORT',
      routerLink: '/',
    },
    {
      icon: 'logout',
      title: 'NAV.SIGN_OUT',
      routerLink: '/',
    },
  ]);


  onTopNavSelect(index: number): void {
    const items = this.topNavItems();
    const nav = items[index];
    if (nav && nav.routerLink) {
      this.router.navigateByUrl(nav.routerLink);
    }
  }

  toggleMenu(): void {
    this.shared.toggleSidebar();
  }

  onGlobalSearch(value: string): void {
    console.log('Search from header:', value);
  }

  onInsuranceProductChange(product: 'medical' | 'motor'): void {
    if (product === 'motor') {
      this.router.navigateByUrl('/motor/pipeline');
      return;
    }

    this.router.navigateByUrl('/home');
  }

  /**
   * Product chosen on the selection page, shown read-only in the header while the switch is off.
   * `productAccessGuard` keeps this valid on every page inside this layout; clients don't choose a product.
   */
  protected readonly selectedProduct = computed<ProductDefinition | null>(() => {
    if (IS_HEADER_PRODUCT_SWITCH_ENABLED || clientToken()) {
      return null;
    }

    const code = selectedProductCode();
    return PRODUCT_CATALOGUE.find((product) => product.code === code && product.isAvailable) ?? null;
  });

  protected showInsuranceProductSwitch(): boolean {
    // Products are chosen once per login on the selection page; the switch below stays for when this is turned back on.
    if (!IS_HEADER_PRODUCT_SWITCH_ENABLED) {
      return false;
    }

    const currentUrl = this.router.url;

    return !(
      this.isMafRoute() ||
      this.isComparisonLink() ||
      currentUrl.includes('/preference-form')
    );
  }

  onLogoutClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.shared.setIntentionalLogout(true);

    void Promise.resolve(this.authService.logout()).catch((error: unknown) => {
      console.error('Provider logout failed. Falling back to legacy redirect logout.', error);
      logoutRedirect(this.shared, this.router, this.dialog);
    });
  }

  isProfileDrawerClosing = false;

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.quoteDrawerService.rightDrawerMode() !== 'audit' || !this.quoteDrawerService.rightDrawerOpened()) {
      return;
    }

    const target = event.target as Node | null;
    const drawerElement = this.auditDrawerRef?.nativeElement;
    if (!target || !drawerElement) {
      return;
    }

    if (!drawerElement.contains(target)) {
      this.quoteDrawerService.closeAuditDrawer();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onDocumentEscape(event: Event): void {
    if (this.quoteDrawerService.rightDrawerMode() !== 'audit' || !this.quoteDrawerService.rightDrawerOpened()) {
      return;
    }

    event.preventDefault();
    this.quoteDrawerService.closeAuditDrawer();
  }

  onProfileIconClick(): void {
    // Open profile drawer without userId - will load current user profile via API
    this.quoteDrawerService.openProfileDrawer(null);
  }
  onProfileDrawerClose(): void {
    if (this.isQuoteBuilderRoute() || this.isComparisonLink()) {
      this.quoteDrawerService.restoreQuoteDrawer();
      return;
    }

    // Keep element in DOM during close animation
    this.isProfileDrawerClosing = true;
    this.quoteDrawerService.setRightDrawerOpened(false);

    // After animation completes, clean up
    setTimeout(() => {
      this.quoteDrawerService.clearRightDrawer();
      this.isProfileDrawerClosing = false;
    }, 450);
  }

  onProfileAnimationStart(): void {
    // No-op — animation handled via onProfileDrawerClose
  }

  onBackButtonClick(): void {
    const currentUrl = this.router.url;
    if (this.isMafRoute()) {
      if (this.isReviewMafOnlineRoute(currentUrl)) {
        this.router.navigateByUrl(currentUrl.replace(/\/maf-online(?:\?.*)?$/, ''));
        return;
      }

      switch (true) {
        case currentUrl.includes('maf-entry'):
          saveToStorage('comparisonNavigationMode', 'back-edit');
          this.router.navigate(['/comparison']);
          break;
        case currentUrl.includes('maf-offline-upload'):
          this.router.navigate(['/maf-entry']);
          break;
        case currentUrl.includes('maf-online-preview'):
          this.router.navigate(['/maf-online']);
          break;
        case currentUrl.includes('maf-online-upload'):
          this.router.navigate(['/maf-online-preview']);
          break;
        case currentUrl.includes('maf-online'):
          this.router.navigate(['/maf-entry']);
          break;
        case currentUrl.includes('maf-uploads'):
          this.router.navigate(['/maf-entry']);
          break;
        case currentUrl.includes('maf-submission'):
          if (this.shared.mafBackButtonDisabled()) {
            return;
          }
          if (this.shared.mafApplicationSubmitted()) {
            this.shared.triggerMafBackButton();
          } else {
            this.router.navigate(['/maf-uploads']);
          }
          break;
        // Add more cases for other MAF screens as needed
        default:
          // Default MAF back navigation logic (optional)
          break;
      }
    }
  }

  protected showGlobalBackButton(): boolean {
    return this.isMafRoute() &&
      !this.isReviewMafOnlineRoute(this.router.url) &&
      !this.shared.mafBackButtonDisabled();
  }

  private isReviewMafOnlineRoute(url: string): boolean {
    return url.includes('/review-queue/application-review/') && url.includes('/maf-online');
  }

  private handleProfileDrawerRouteChange(previousUrl: string, nextUrl: string): void {
    const isProfileDrawerOpen = this.quoteDrawerService.rightDrawerMode() === 'profile';
    const leavingUserManagement = previousUrl.includes('/user-management') && !nextUrl.includes('/user-management');

    if (isProfileDrawerOpen && leavingUserManagement) {
      this.quoteDrawerService.closeProfileDrawer();
    }
  }

  // toggleSideBar(): void {
  //   this.isDrawerOpen.set(!this.isDrawerOpen());
  //   if (this.quoteDrawerService.rightDrawerMode()) {
  //     this.quoteDrawerService.setRightDrawerOpened(this.isDrawerOpen());
  //   }
  //   this.quoteDrawerService.scrollUpdateSignal.set(true);
  // }

    toggleSideBar(): void {
  const nextOpened = !this.quoteDrawerService.rightDrawerOpened();
  this.isDrawerOpen.set(nextOpened);
  this.quoteDrawerService.setRightDrawerOpened(nextOpened);
  this.quoteDrawerService.scrollUpdateSignal.set(true);
}

  protected shouldShowRightDrawer(): boolean {
    return this.quoteDrawerService.rightDrawerMode() !== null;
  }

  private mapUserDetails(user: any): UserManagementRow {
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';

    return {
      id: user?.id || user?.userId || '',
      firstName,
      lastName,
      fullName: user?.fullName || `${firstName} ${lastName}`.trim(),
      emailId: user?.emailId || user?.email || '',
      mobileNumber: user?.mobileNumber || user?.phoneNumber || '',
      whatsAppNumber: user?.whatsAppNumber || null,
      employeeId: user?.employeeId || null,
      gender: user?.gender || '',
      roles: this.mapList(user?.roles),
      departments: this.mapList(user?.departments),
      businessTypes: this.mapList(user?.businessTypes),
      commissionRate: Number(user?.commissionRate ?? 0),
      paymentTerms: user?.paymentTerms || null,
      insuranceCategory: user?.insuranceCategory || null,
      reportingManagerName: user?.reportingManagerName || '',
      profileImage: user?.profileImage,
      reportingManagerImage: user?.reportingManagerImage || null,
      status: user?.status,
      hasDependencies: !!user?.hasDependencies,
      createdOn: user?.createdOn || '',
      createdByName: user?.createdByName || '',
      lastLogin: DateTimeConversionHelper.toDateTime(user?.lastLogin) || null,
      dob: user?.dateOfBirth,
    } as UserManagementRow;
  }

  private mapList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map(item =>
          (
            typeof item === 'string'
              ? item
              : String(
                item?.name ??
                item?.label ??
                item ??
                ''
              )
          ).trim()
        )
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return value.split(',').map(item => item.trim()).filter(Boolean);
    }

    return [];
  }

  private readonly apiService = inject(ApiService, { optional: true });

}
